import CardGrid from "@components/Layout/CardGrid"
import MaxWidthContainer from "@components/Layout/MaxWidthContainer"
import MaxWidthContent from "@components/Layout/MaxWidthContent"
import MenuItemAccordionCheckbox from "@components/MenuItemAccordionCheckbox"
import ProductCard from "@components/ProductCard"
import colors from "@constants/colors"
import { columnsInMaxWidth } from "@constants/layout"
import { body, bodyBold } from "@constants/typography"
import { DatoResponsiveImage } from "@dato/types"
import { toggleKeyInArray } from "@utils/index"
import { LaptopUpOnly } from "@utils/MediaQueries"
import { getProductUrl } from "@utils/urls"
import React from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"

const Container = styled.div`
  margin-top: 20px;
  border-top: solid 1px ${colors.divider};

  ${up("laptop")} {
    display: flex;
    border: none;
  }
`

const Sidebar = styled.div`
  display: none;

  ${up("laptop")} {
    position: relative;
    display: block;
    padding-top: 20px;
    padding-right: calc(${columnsInMaxWidth(0.5)});
  }
`

const Buttons = styled.div`
  align-items: center;
  justify-content: flex-start;
  display: flex;
  margin-top: 26px;
`

const baseButtonStyles = css`
  outline: none;
  border: none;
  background: none;

  &:hover {
    cursor: pointer;
  }

  &:focus,
  &:active {
    outline: none;
  }
`

const Apply = styled.button`
  ${baseButtonStyles}
  ${bodyBold}
  margin-right: 8px;
  color: ${colors.primary};
`

const Clear = styled.button`
  ${baseButtonStyles}
  ${body}
  color: ${colors.slateDark};
`

interface ProductsProps {
  containerRef?: any
  showFilters?: boolean
  filters?: any
  activeFilters?: any
  onFilter?: (filters: any) => void
  onClearFilters?: () => void
  products: any
  hidePrice?: boolean
  defaultProductImage?: DatoResponsiveImage
  make?: string
  model?: string
  year?: string
  trim?: string
  /** When true, appends " each" beside price when product.soldAs === "single". Used only on Pro Series category list. */
  showEachForSingleSoldAs?: boolean
}

const Products: React.FC<ProductsProps> = ({
  containerRef,
  showFilters,
  filters,
  activeFilters,
  onFilter,
  onClearFilters,
  products,
  hidePrice,
  defaultProductImage,
  make = undefined,
  model = undefined,
  year = undefined,
  trim = undefined,
  showEachForSingleSoldAs = false,
}) => {
  const formattedProducts = React.useMemo(() => {
    return products.map((p) => ({
      id: getProductUrl(p.slug),
      url: getProductUrl(p.slug, make, model, year, trim),
      image: p.thumbnail?.responsiveImage?.src || defaultProductImage?.src,
      title: p.productName,
      price: p.price,
      compareAtPrice: p.compareAtPrice,
      discountedPrice: p.discountedPrice,
      isDiscounted: p.isDiscounted,
      hidePrice,
      tags: p.tags,
      productCategory: p.productCategory,
      priceSuffix:
        showEachForSingleSoldAs && p.soldAs === "single" ? " each" : undefined,
    }))
  }, [products, showEachForSingleSoldAs])

  // console.log("formattedProducts", formattedProducts)
  // console.log("products", products)
  const [pristine, setPristine] = React.useState<boolean>(true)
  const [selectedFilters, setSelectedFilters] = React.useState<any>([])

  React.useEffect(() => {
    setSelectedFilters(activeFilters)
  }, [activeFilters])

  const formattedFilters = React.useMemo(() => {
    const _formattedFilters = {}

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters)
        .filter((key) => filters[key].length > 1)
        .forEach((key) => {
          _formattedFilters[key] = filters[key].map((f) => {
            const filter = typeof f === "string" ? f : f.name
            return {
              label: filter,
              checked:
                selectedFilters[key] &&
                selectedFilters[key].indexOf(filter) !== -1,
              onClick: () => {
                setSelectedFilters({
                  ...selectedFilters,
                  [key]: toggleKeyInArray(filter, selectedFilters[key]),
                })

                if (pristine) {
                  setPristine(false)
                }
              },
            }
          })
        })
    }
    return _formattedFilters
  }, [filters, activeFilters, selectedFilters])

  const handleClearFilters = () => {
    if (Object.keys(activeFilters).length > 0 && onClearFilters) {
      onClearFilters()
    } else {
      setSelectedFilters({})
    }
  }

  const hasFilters = Object.keys(formattedFilters).length > 0

  return (
    <MaxWidthContainer noMobilePadding>
      {hasFilters && showFilters && (
        <LaptopUpOnly>
          <MaxWidthContent cols={2.5}>
            <Sidebar>
              {Object.keys(formattedFilters).length > 0 &&
                Object.keys(formattedFilters).map((key) => (
                  <MenuItemAccordionCheckbox
                    key={key}
                    label={key}
                    items={formattedFilters[key]}
                    activeItemCount={
                      selectedFilters[key] ? selectedFilters[key].length : 0
                    }
                  />
                ))}
              {!pristine && (
                <Buttons>
                  <Apply
                    onClick={() => {
                      if (onFilter) {
                        onFilter(selectedFilters)
                      }
                    }}
                  >
                    Apply
                  </Apply>
                  <Clear onClick={handleClearFilters}>Clear</Clear>
                </Buttons>
              )}
            </Sidebar>
          </MaxWidthContent>
        </LaptopUpOnly>
      )}
      <MaxWidthContent cols={showFilters ? 9.5 : 12}>
        <Container>
          <CardGrid
            containerRef={containerRef}
            cards={formattedProducts}
            CardComponent={ProductCard}
          />
        </Container>
      </MaxWidthContent>
    </MaxWidthContainer>
  )
}

export default Products
